import {
  BaseConnector,
  ConnectorCapability,
  ConnectorStatus,
  type ConnectorConnectOptions,
  type ConnectorHealthResult,
  type ConnectorPriceResult,
  type ConnectorProductRef,
  type ConnectorProductResult,
  type ConnectorSearchOptions,
  type ConnectorStockResult,
} from '@/modules/connector-sdk'
import { integrationService } from '@/modules/integration-center'
import type { SearchResult } from '@/modules/search-engine'
import { sessionManager } from '@/modules/session-manager'

import { PautaNotImplementedError, PautaSessionError } from './PautaErrors'
import { pautaMapper } from './PautaMapper'
import { pautaParser } from './PautaParser'
import {
  PAUTA_CONNECTOR_ID,
  PAUTA_CONNECTOR_VERSION,
  PAUTA_INTEGRATION_ID,
  type PautaHealthReport,
  type PautaProduct,
} from './PautaTypes'

/**
 * PautaConnector — reference connector for MasterHub.
 * Sprint 3.2: architecture + simulated search. No scraping / Playwright.
 */
export class PautaConnector extends BaseConnector {
  private lastConnectionAt: string | null = null
  private lastError: string | null = null

  constructor() {
    super({
      meta: {
        id: PAUTA_CONNECTOR_ID,
        name: 'Pauta Distribuidora',
        version: PAUTA_CONNECTOR_VERSION,
        integrationId: PAUTA_INTEGRATION_ID,
      },
      capabilities: [
        ConnectorCapability.LOGIN,
        ConnectorCapability.SEARCH,
        ConnectorCapability.PRICE,
        ConnectorCapability.STOCK,
      ],
    })
  }

  /** Prefer Integration Center record when present. */
  private resolveIntegrationId(): string {
    const fromCenter = integrationService.get(PAUTA_INTEGRATION_ID)
    return fromCenter?.id ?? PAUTA_INTEGRATION_ID
  }

  protected async onConnect(options: ConnectorConnectOptions): Promise<void> {
    const integrationId = options.integrationId ?? this.resolveIntegrationId()

    // Architecture only — no real login / Playwright.
    // Creates a Session Manager session so connectors never own login state.
    const existing = this.getSessionId()
      ? sessionManager.getSession(this.getSessionId()!)
      : sessionManager.getActiveSession(integrationId)

    if (existing && existing.status === 'active') {
      const validation = sessionManager.validateSession(existing.id)
      if (validation.valid && validation.session) {
        this.setSessionId(validation.session.id)
        this.lastConnectionAt = new Date().toISOString()
        this.lastError = null
        sessionManager.updateSession(validation.session.id, {
          status: 'active',
          lastError: null,
        })
        return
      }
    }

    const session = sessionManager.createSession({
      integrationId,
      status: 'active',
      cookies: '',
      headers: {},
      tokens: {},
      browser: {
        contextKey: null,
        storageStateRef: null,
        userAgent: null,
        headless: true,
      },
    })

    sessionManager.updateSession(session.id, {
      status: 'active',
      lastError: null,
    })

    this.setSessionId(session.id)
    this.lastConnectionAt = new Date().toISOString()
    this.lastError = null
  }

  /** Reserved — real login will throw until Playwright sprint. */
  protected assertScrapingReady(): never {
    throw new PautaNotImplementedError('scraping/Playwright login')
  }

  protected async onDisconnect(): Promise<void> {
    const sessionId = this.getSessionId()
    if (sessionId) {
      sessionManager.invalidateSession(sessionId, 'Pauta disconnect')
      sessionManager.removeSession(sessionId)
    }
    this.setSessionId(null)
  }

  protected async onRefreshSession(): Promise<void> {
    const sessionId = this.getSessionId()
    if (!sessionId) {
      this.lastError = 'Nenhuma sessão para refresh'
      throw new PautaSessionError(this.lastError)
    }

    const validation = sessionManager.validateSession(sessionId)
    if (!validation.valid || !validation.session) {
      this.lastError = validation.reason ?? 'Sessão inválida'
      sessionManager.invalidateSession(sessionId, this.lastError)
      // Recreate simulated session (still no Playwright login).
      await this.onConnect({ integrationId: this.resolveIntegrationId(), force: true })
      return
    }

    sessionManager.updateSession(sessionId, {
      status: 'active',
      lastError: null,
    })
    this.lastConnectionAt = new Date().toISOString()
    this.lastError = null
  }

  protected async onSearchProducts(
    query: string,
    options?: ConnectorSearchOptions,
    _ref?: ConnectorProductRef,
  ): Promise<ConnectorProductResult[]> {
    const products = this.loadSimulatedProducts(query, options?.limit)
    return pautaMapper.toConnectorProducts(products)
  }

  protected async onGetStock(sku: string): Promise<ConnectorStockResult> {
    const products = this.loadSimulatedProducts(sku, 5)
    const hit = products.find((p) => p.codigo.toLowerCase() === sku.toLowerCase()) ?? products[0]
    return {
      sku,
      available: (hit?.estoque ?? 0) > 0,
      quantity: hit?.estoque ?? null,
      warehouse: 'Pauta',
      updatedAt: new Date().toISOString(),
    }
  }

  protected async onGetPrice(sku: string): Promise<ConnectorPriceResult> {
    const products = this.loadSimulatedProducts(sku, 5)
    const hit = products.find((p) => p.codigo.toLowerCase() === sku.toLowerCase()) ?? products[0]
    return {
      sku,
      price: hit?.preco ?? null,
      currency: hit?.moeda ?? 'BRL',
      updatedAt: new Date().toISOString(),
    }
  }

  /**
   * Maps simulated Pauta rows → SearchResult (canonical for Search Engine / UI).
   */
  searchAsSearchResults(query: string, limit = 20): SearchResult[] {
    const products = this.loadSimulatedProducts(query, limit)
    return pautaMapper.toSearchResults(products, { connectorName: this.meta.name })
  }

  async health(): Promise<ConnectorHealthResult> {
    const report = this.getHealthReport()
    const base = await super.health()
    return {
      ...base,
      status: this.getStatus(),
      healthy: this.getStatus() === ConnectorStatus.CONNECTED && !report.lastError,
      message: report.lastError ?? 'Pauta health OK (arquitetura — sem ping remoto)',
      details: {
        ...base.details,
        ...report,
      },
    }
  }

  getHealthReport(): PautaHealthReport {
    const sessionId = this.getSessionId()
    const session = sessionId ? sessionManager.getSession(sessionId) : null
    return {
      status: this.getStatus(),
      lastConnectionAt: this.lastConnectionAt,
      version: this.meta.version,
      lastError: this.lastError ?? session?.lastError ?? null,
      sessionId,
      integrationId: this.resolveIntegrationId(),
    }
  }

  /**
   * Simulated catalog — replace with Playwright/API fetch in a future sprint.
   * Always goes through Parser → Mapper (never raw HTML/DOM).
   */
  private loadSimulatedProducts(query: string, limit = 20): PautaProduct[] {
    const q = query.trim().toLowerCase() || 'produto'
    const raw = [
      {
        code: 'PT-NB-14-I5',
        brand: 'Dell',
        name: `Notebook Dell Latitude — ${q}`,
        price: 4299.9,
        stock: 7,
        url: 'https://pauta.example/pt-nb-14-i5',
        prazo: 3,
        currency: 'BRL',
      },
      {
        sku: 'PT-SSD-1TB',
        manufacturer: 'Kingston',
        title: `SSD Kingston NV2 1TB — ${q}`,
        price: '489,90',
        qty: 42,
        link: 'https://pauta.example/pt-ssd-1tb',
        delivery: '2 dias',
      },
      {
        code: 'PT-RAM-16',
        brand: 'Corsair',
        name: `Memória Corsair 16GB DDR5 — ${q}`,
        price: 399,
        stock: 0,
        url: 'https://pauta.example/pt-ram-16',
        prazo: 5,
      },
    ]

    const parsed = pautaParser.parseProducts(raw)
    return parsed.slice(0, Math.max(1, limit ?? 20))
  }
}
